import { useState } from 'react';
import { Stack, TextField, MenuItem, Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { LoadingState } from '../../components/LoadingState';
import { profitabilityApi } from '../../api/profitability.api';

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const COST_TYPES = ['material', 'labour', 'logistics', 'travel', 'accommodation', 'miscellaneous', 'rework'];

export function PnlPage() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [granularity, setGranularity] = useState('monthly');
  const { data, isLoading } = useQuery({ queryKey: ['pnl', year, granularity], queryFn: () => profitabilityApi.pnl(year, granularity) });

  const label = (p: number) => (granularity === 'monthly' ? MONTHS[p - 1] : granularity === 'quarterly' ? `Q${p}` : String(p));

  return (
    <>
      <PageHeader title="P&L Statements" subtitle="Revenue, cost breakdown and margins by period." />
      <Stack direction="row" spacing={2} mb={2}>
        <TextField select size="small" label="Year" value={year} onChange={(e) => setYear(Number(e.target.value))} sx={{ width: 120 }}>
          {[year + 1, year, year - 1, year - 2].map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Granularity" value={granularity} onChange={(e) => setGranularity(e.target.value)} sx={{ width: 160 }}>
          <MenuItem value="monthly">Monthly</MenuItem><MenuItem value="quarterly">Quarterly</MenuItem><MenuItem value="yearly">Yearly</MenuItem>
        </TextField>
      </Stack>
      <Card>
        <CardContent sx={{ p: 0, overflowX: 'auto' }}>
          {isLoading ? <LoadingState /> : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Period</TableCell>
                  <TableCell align="right">Revenue</TableCell>
                  {COST_TYPES.map((c) => <TableCell key={c} align="right" sx={{ textTransform: 'capitalize' }}>{c}</TableCell>)}
                  <TableCell align="right">Total Cost</TableCell>
                  <TableCell align="right">Gross</TableCell>
                  <TableCell align="right">Net</TableCell>
                  <TableCell align="right">Net %</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.periods ?? []).map((p) => (
                  <TableRow key={p.period} hover>
                    <TableCell>{label(p.period)}</TableCell>
                    <TableCell align="right">{inr(p.revenue)}</TableCell>
                    {COST_TYPES.map((c) => <TableCell key={c} align="right">{p.costByType[c] ? inr(p.costByType[c]) : '—'}</TableCell>)}
                    <TableCell align="right">{inr(p.totalCost)}</TableCell>
                    <TableCell align="right">{inr(p.grossProfit)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: p.netProfit < 0 ? 'error.main' : 'success.main' }}>{inr(p.netProfit)}</TableCell>
                    <TableCell align="right">{p.netMarginPct}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </>
  );
}
